<template>
  <main role="main" class="py-3">
    <b-container class="container-x">
      <!-- Title Section -->
      <div class="d-flex flex-row align-items-center justify-content-between mb-3 border-bottom pb-2">
        <h4 class="m-0 text-dark font-weight-bold">{{ $t("manageConfigulation") }}</h4>
        <b-button variant="primary" size="sm" class="d-flex align-items-center px-3 btn-create-custom" to="/admin-settings/api-config/new" v-if="isUserAdmin()">
          <b-icon icon="plus" class="mr-1"></b-icon> {{ $t("createNew") }}
        </b-button>
      </div>

      <!-- Config Filter Card -->
      <b-card class="border mb-3 filter-card">
        <b-form class="d-flex flex-row align-items-center mb-0" @submit.prevent="onClickSearchBtn">
          <b-form-select class="mr-2" :options="filterCoreType" v-model="filterStatus" style="max-width: 320px;"/>
          <b-button type="submit" variant="primary" style="min-width: 100px;">{{ $t("search") }}</b-button>
          <div v-if="totalItems && totalItems > 0" class="ml-auto small text-secondary">
            {{ $t("resultCount", { count: totalItems }) }}
          </div>
        </b-form>
      </b-card>

      <!-- Grouped Configs Lists -->
      <div v-if="isLoading" class="py-5 text-center">
        <b-spinner variant="primary"></b-spinner>
        <div class="text-muted mt-2 small">{{ $t("loadingData") }}</div>
      </div>

      <div v-else>
        <div v-if="items.length === 0" class="border card p-5 text-center text-secondary">
          {{ $t("noData") }}
        </div>
        <div v-else v-for="group in groupedItems" :key="group.coreType" class="mb-4">
          <div class="group-header" @click="toggleGroup(group.coreType)">
            <div class="d-flex align-items-center flex-wrap">
              <span 
                class="badge text-uppercase font-weight-bold mr-3" 
                :class="group.hasActive ? 'badge-primary' : 'badge-light text-secondary border'"
                style="font-size: 0.8rem; letter-spacing: 0.5px; padding: 6px 12px; border-radius: 4px;"
              >
                {{ group.coreType }}
              </span>
              <span class="font-weight-bold text-dark mr-2" style="font-size: 0.95rem;">
                {{ group.groupItems[0] ? group.groupItems[0].description : '' }}
              </span>
              <span class="badge badge-light text-muted border px-2 py-1 ml-1" style="font-size: 0.75rem;">
                {{ group.groupItems.length }} {{ $t("configurations") }}
              </span>
            </div>
            <b-icon :icon="isGroupCollapsed(group.coreType) ? 'chevron-down' : 'chevron-up'" class="text-secondary transition-icon" :class="{ 'collapsed': isGroupCollapsed(group.coreType) }" style="font-size: 1.1rem;"></b-icon>
          </div>          <!-- Table Card -->
          <b-collapse :visible="!isGroupCollapsed(group.coreType)">
            <b-card class="border p-0 mb-3" no-body>
              <b-table hover :items="group.groupItems" :fields="tableFields" class="goicuoctable border-0 mb-0" show-empty>
                <template v-slot:empty>
                  <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
                </template>
                <template v-slot:cell(apiUrl)="data">
                  <span class="text-dark text-truncate d-inline-block" style="max-width: 300px;" v-b-tooltip.bottom.hover :title="data.item.apiUrl">
                    {{ data.item.apiUrl }}
                  </span>
                </template>
                <template v-slot:cell(active)="data">
                  <span class="badge px-3 py-1.5 font-weight-bold"
                        :style="{
                          backgroundColor: data.item.active ? '#e6f4ea' : '#fce8e6',
                          color: data.item.active ? '#137333' : '#c5221f',
                          fontSize: '0.75rem',
                          borderRadius: '30px'
                        }">
                    {{ getStatus(data.item.active) }}
                  </span>
                </template>
                <template v-slot:cell(actions)="data">
                  <b-dropdown size="sm" variant="link" toggle-class="text-decoration-none p-1" no-caret right menu-class="shadow-sm border" boundary="viewport" dropup>
                    <template v-slot:button-content>
                      <b-icon icon="three-dots-vertical" class="text-secondary"/>
                    </template>
                    <b-dropdown-item :to="`/admin-settings/api-config/edit/${data.item.id}`">{{ $t("updateApiConfig") }}</b-dropdown-item>
                    <b-dropdown-item-button  @click="onClickChangeStatus(data.item.id)">{{ $t("onOff") }}</b-dropdown-item-button>
                  </b-dropdown>
                </template>
              </b-table>
            </b-card>
          </b-collapse>
        </div>
      </div>
    </b-container>
  </main>
</template>

<style lang="scss" scoped>
.container-x {
  max-width: 1200px;
}
.card {
  // border-radius: 8px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  background-color: #f4f9ff;
  border: 1px solid #eef2f6;
  &.filter-card {
    border-radius: 8px !important;
  }
}
.font-weight-bold {
  font-weight: 600 !important;
}
.group-header {
  background-color: #f4f9ff;
  border: 1px solid #e2e8f0;
  // border-radius: 8px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    background-color: #f8fafc;
    border-color: #cbd5e1;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }
}
.transition-icon {
  transition: transform 0.2s ease;
  &.collapsed {
    transform: rotate(0deg);
  }
}
.goicuoctable {
  background-color: #f4f9ff;
  border-radius: 8px;
  ::v-deep th {
    background-color: #f4f9ff;
    color: #475569;
    font-size: 0.8rem;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.5px;
    padding: 8px 12px;
    border-bottom: 1px solid #e2e8f0;
    &:first-child {
      border-top-left-radius: 8px;
    }
    &:last-child {
      border-top-right-radius: 8px;
    }
  }
  ::v-deep tbody tr:last-child {
    td:first-child {
      border-bottom-left-radius: 8px;
    }
    td:last-child {
      border-bottom-right-radius: 8px;
    }
  }
  ::v-deep td {
    padding: 10px 12px;
    vertical-align: middle;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
  }
  ::v-deep tbody tr {
    transition: background-color 0.15s ease;
    &:hover {
      background-color: #f8fafc !important;
    }
  }
}
.indicator-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
</style>

<script lang="ts">
  import {Component, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import {BvTableFieldArray} from "bootstrap-vue";

  import * as subscriptionPlanService from "@/service/subscriptionPlanService";

  @Component
  export default class ManagerConfig extends BaseComponent {

    totalItems = 0;
    isLoading = false;
    items: any[] = [];

    filterStatus: string = '';
    filterCoreType = [];

    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "ID",
        thClass: "text-nowrap",
      },
      {
        key: "apiUrl",
        label: "URL",
        thClass: "text-nowrap",
      },
      // {
      //   key: "coreType",
      //   label: "Core Type",
      //   thClass: "text-nowrap",
      //   tdClass: "text-break",
      // },
      // {
      //   key: "description",
      //   label: this.$t("packageType").toString(),
      //   thClass: "text-nowrap",
      // },
      {
        key: "maxMeetings",
        label: this.$t("numMeeting").toString(),
        thClass: "text-nowrap",
      },
      {
        key: "maxParticipants",
        label: this.$t("meetingParticipant").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "active",
        label: this.$t("status").toString(),
        formatter: this.getStatus,
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "actions",
        label: "",
        thClass: "text-nowrap",
      }
    ]; 

    mounted() {
      this.fetchPlans();
    }

    getStatus(value) {
      return value ? this.$t("active").toString()  : this.$t("unactive").toString() ;
    }

    async fetchPlans() {
      try {
        this.isLoading = true;
        this.filterCoreType = await subscriptionPlanService.getPlanTypeList();
        this.filterCoreType.unshift({ value: '', text: this.$t("all").toString() });
        await this.loadData();
      } catch (e) {
        this.toastError(e);
      } finally {
        this.isLoading = false;
      }
    }

    async loadData() {
      try {
        this.isLoading = true;
        const res = await subscriptionPlanService.getCorePlanTypeList({ type: this.filterStatus });
        let rawItems = [];
        if (Array.isArray(res)) {
          rawItems = res;
        } else if (res && typeof res === "object") {
          rawItems = (res as any).data || [];
        }
        // Sort active configurations first, then by id descending
        this.items = _.orderBy(rawItems, ['active', 'id'], ['desc', 'desc']);
        this.totalItems = this.items.length;
      } catch (e) {
        this.toastError(e);
      } finally {
        this.isLoading = false;
      }
    }

    collapsedGroups: Record<string, boolean> = {};

    toggleGroup(coreType: string) {
      this.$set(this.collapsedGroups, coreType, !this.collapsedGroups[coreType]);
    }

    isGroupCollapsed(coreType: string) {
      return !!this.collapsedGroups[coreType];
    }

    get groupedItems() {
      const grouped = _.groupBy(this.items, 'coreType');
      const groupsWithStatus = Object.keys(grouped).map(coreType => {
        const groupItems = grouped[coreType];
        // Sort items inside this group alphabetically by apiUrl
        const sortedItems = _.orderBy(groupItems, [item => item.apiUrl?.toLowerCase()], ['asc']);
        const hasActive = sortedItems.some(item => item.active === 1 || item.active === true);
        return {
          coreType,
          hasActive,
          groupItems: sortedItems
        };
      });
      // Sort groups with active items first, then alphabetical by coreType name
      return _.orderBy(groupsWithStatus, ['hasActive', 'coreType'], ['desc', 'asc']);
    }

    onClickSearchBtn() {
      this.loadData();
    }

    async onClickChangeStatus(id) {
      try {
        await subscriptionPlanService.actionCorePlanType(id);   
        await this.loadData();       
      } catch (error) {
        this.toastError(error);
      }
    }
  }
</script>
  